import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import OverviewHeader from "./OverviewHeader";
import StatsCards from "./StatsCards";
import RevenueChart from "./RevenueChart";
import StudentStrengthChart from "./StudentStrengthChart";
import AdmissionsTable from "./AdmissionsTable";
import Activities from "./Activities";
import CampusActivities from "./CampusActivities";

// Campus dashboards
import PerumbakkamDashboard from "../schoolbranches/Perumbakkam";
import Kvr from "../schoolbranches/Kvr";
import Thiruvanmiyur from "../schoolbranches/Thiruvanmiyur";
import Thirumudivakkam from "../schoolbranches/Thirumudivakkam";
import Hyderabad from "../schoolbranches/Hyderabad";

// Group dashboards
import TransportDashboard from "../transport/TransportDashboard";
import HumanResourcesDashboard from "../hr/HrDashboard";
import AcademicDashboard from "../academics/AcademicDashboard";
import BestSchool from "../BestSchool";

import { fetchDashboardDataFromSheet } from "../../api/sheetsApi";

const DashboardPage = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Refs for all dashboards
  const dashboardRef = useRef(null);
  const perumbakkamRef = useRef(null);
  const kvrRef = useRef(null);
  const thiruvanmiyurRef = useRef(null);
  const thirumudivakkamRef = useRef(null);
  const hyderabadRef = useRef(null);
  const transportRef = useRef(null);
  const hrRef = useRef(null);
  const academicsRef = useRef(null);
  const bestSchoolRef = useRef(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const data = await fetchDashboardDataFromSheet();
        setDashboardData(data);
      } catch (err) {
        console.error(err);
        setError("Unable to load dashboard data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f6fb] p-2 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading data...</p>
        </div>
      </div>
    );
  }
  if (error) return <p>{error}</p>;

  return (
    <>
      {/* Page 1 content (visible in UI) */}
      <div ref={dashboardRef} className="p-2 bg-gray-100 min-h-[100dvh] space-y-6">
        <OverviewHeader
          dashboardRef={dashboardRef}
          perumbakkamRef={perumbakkamRef}
          kvrRef={kvrRef}
          thiruvanmiyurRef={thiruvanmiyurRef}
          thirumudivakkamRef={thirumudivakkamRef}
          hyderabadRef={hyderabadRef}
          transportRef={transportRef}
          hrRef={hrRef}
          academicsRef={academicsRef}
          bestSchoolRef={bestSchoolRef}
          currentMonth={dashboardData?.currentMonth}
        />

        <StatsCards data={dashboardData} />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <RevenueChart data={dashboardData?.admissionsPipelineData} />
          <div className="md:col-span-2">
            <StudentStrengthChart data={dashboardData?.studentStrengthGradeWiseData} />
          </div>
        </div>

        <div>
          <AdmissionsTable data={dashboardData?.studentStrengthGradeWiseData} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Activities data={dashboardData?.admissionsPipelineData} />
          <CampusActivities
            data={dashboardData?.campusActivitiesData}
            title={dashboardData?.campusActivitiesTitle}
          />
        </div>
      </div>

      {/* Hidden dashboards (off-screen, capturable) */}
      <div ref={perumbakkamRef} style={{ position: "absolute", left: "-9999px", width: "100%" }}>
        <PerumbakkamDashboard />
      </div>
      <div ref={kvrRef} style={{ position: "absolute", left: "-9999px", width: "100%" }}>
        <Kvr />
      </div>
      <div ref={thiruvanmiyurRef} style={{ position: "absolute", left: "-9999px", width: "100%" }}>
        <Thiruvanmiyur />
      </div>
      <div ref={thirumudivakkamRef} style={{ position: "absolute", left: "-9999px", width: "100%" }}>
        <Thirumudivakkam />
      </div>
      <div ref={hyderabadRef} style={{ position: "absolute", left: "-9999px", width: "100%" }}>
        <Hyderabad />
      </div>
      <div ref={transportRef} style={{ position: "absolute", left: "-9999px", width: "100%" }}>
        <TransportDashboard />
      </div>
      <div ref={hrRef} style={{ position: "absolute", left: "-9999px", width: "100%" }}>
        <HumanResourcesDashboard />
      </div>
      <div ref={academicsRef} style={{ position: "absolute", left: "-9999px", width: "100%" }}>
        <AcademicDashboard />
      </div>
      <div ref={bestSchoolRef} style={{ position: "absolute", left: "-9999px", width: "100%" }}>
        <BestSchool />
      </div>
    </>
  );
};

export default DashboardPage;
